import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Pagination } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { servicesService } from '../../services/api/servicesService';
import { useAuth } from '../../context/AuthContext';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import ContactForServiceModal from '../common/ContactForServiceModal/ContactForServiceModal';
import CompactBusinessRatingLine from '../common/CompactBusinessRatingLine/CompactBusinessRatingLine';
import { getTradeTheme } from '../../constants/tradeTheme';
import EmptyMatchWaitlist from '../EmptyMatchWaitlist/EmptyMatchWaitlist';
import SEO from '../common/SEO/SEO';
import { buildTradeProvidersSeo } from '../common/SEO/seoHelpers';
import {
  fingerprintWizardState,
  getOrCreateWizardClientSessionId,
  getPersistedWizardSearchRequestId,
} from '../../utils/wizardClientSession';
import { consumeSendInquiryResume } from '../../utils/sendInquiryResume';
import { getStorefrontPath } from '../../utils/storefrontPath';
import {
  buildSearchEntryPath,
  buildSearchWizardState,
  buildTaskStepPath,
  buildTimingStepPath,
  formatTradeProCount,
  getSearchTaskLabel,
  getSearchTimingLabel,
  getSearchTradeLabel,
  readServiceSearchParams,
  selectSearchResultRows,
} from '../../utils/serviceSearch';
import {
  applySearchFiltersAndSort,
  mapServiceRowToSearchCard,
  SEARCH_RESULT_FILTERS,
  SEARCH_RESULT_SORTS,
} from '../../utils/wizardSearchProviderCard';
import {
  PageWrapper,
  HeaderSection,
  HeaderInner,
  HeaderTitle,
  AnswerChipRow,
  AnswerChip,
  FilterBar,
  FilterBarInner,
  FilterLabel,
  FilterPillButton,
  SortGroup,
  SortLabel,
  SortButton,
  ResultsContainer,
  ResultsNotice,
  ProvidersGrid,
  ResultsPaginationWrap,
  ResultsPaginationSummary,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  CardWrapper,
  CardAccentBar,
  CardBody,
  CardTopRow,
  CardAvatar,
  CardMeta,
  CardName,
  LocationText,
  RatingsBlock,
  NoReviews,
  CardPricePanel,
  CardTagline,
  CardPrice,
  CardPriceCaption,
  BadgeRow,
  CardBadge,
  CardCTARow,
  PrimaryCtaButton,
  SecondaryCtaLink,
  ProvidersLoadingState,
  ProvidersLoadingTitle,
} from './Serviceproviders.styled';

/** 3×3 cards per page */
const SEARCH_RESULTS_PAGE_SIZE = 9;

/** How many extra top matches the contact sheet offers to include. */
const ALSO_SEND_COUNT = 2;

const AVATAR_COLORS = [
  ['#0D4EFE', '#01EBB2'],
  ['#7C3AED', '#06B6D4'],
  ['#DC2626', '#F59E0B'],
  ['#059669', '#3B82F6'],
  ['#DB2777', '#F97316'],
];

function getColorPair(businessId) {
  const index = parseInt(String(businessId).replace(/\D/g, '') || '0', 10) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

function getInitials(name) {
  return String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function ProviderCard({ provider, serviceSearch, onRequestQuote }) {
  const [colorStart, colorEnd] = getColorPair(provider.BusinessId);
  const profilePath = getStorefrontPath(provider.BusinessSlug || provider.BusinessId);
  const phoneDigits = String(provider.BusinessPhone || '').replace(/\D/g, '');

  return (
    <CardWrapper aria-label={provider.BusinessName}>
      <CardAccentBar $start={colorStart} $end={colorEnd} />
      <CardBody>
        <CardTopRow>
          <CardAvatar $start={colorStart} $end={colorEnd}>
            {provider.BusinessPhotoUrl ? (
              <img src={provider.BusinessPhotoUrl} alt="" />
            ) : (
              getInitials(provider.BusinessName)
            )}
          </CardAvatar>
          <CardMeta>
            <CardName>{provider.BusinessName}</CardName>
            <LocationText>{provider.city}</LocationText>
            <RatingsBlock>
              {provider.rating != null ? (
                <CompactBusinessRatingLine
                  source="nky"
                  rating={provider.rating}
                  reviewCount={provider.reviewCount}
                />
              ) : (
                <NoReviews>No NKY Connect reviews yet</NoReviews>
              )}
              {provider.googleRating != null ? (
                <CompactBusinessRatingLine
                  source="google"
                  rating={provider.googleRating}
                  reviewCount={provider.googleReviewCount}
                />
              ) : null}
            </RatingsBlock>
          </CardMeta>
        </CardTopRow>

        <CardPricePanel>
          {provider.tagline ? (
            <CardTagline>
              <CheckCircleRoundedIcon aria-hidden />
              {provider.tagline}
            </CardTagline>
          ) : null}
          <CardPrice>{provider.priceLabel}</CardPrice>
          {provider.taskLabel ? (
            <CardPriceCaption>for {provider.taskLabel}</CardPriceCaption>
          ) : null}
        </CardPricePanel>

        <BadgeRow>
          {provider.hasFreeQuotes ? (
            <CardBadge $tone="green">
              <RequestQuoteRoundedIcon aria-hidden />
              Free quotes
              {provider.quotingMethodLabels.length > 0
                ? ` · ${provider.quotingMethodLabels.join(', ')}`
                : ''}
            </CardBadge>
          ) : null}
          {provider.hasFinancing ? (
            <CardBadge $tone="green">
              <AccountBalanceWalletRoundedIcon aria-hidden />
              Financing
            </CardBadge>
          ) : null}
          {provider.availabilityLabel ? (
            <CardBadge $tone={provider.availabilityUrgent ? 'green' : 'neutral'}>
              <ScheduleRoundedIcon aria-hidden />
              {provider.availabilityLabel}
            </CardBadge>
          ) : null}
        </BadgeRow>
      </CardBody>

      <CardCTARow>
        <PrimaryCtaButton type="button" onClick={() => onRequestQuote(provider)}>
          <RequestQuoteRoundedIcon aria-hidden />
          Request a quote
        </PrimaryCtaButton>
        {phoneDigits ? (
          <SecondaryCtaLink href={`tel:${phoneDigits}`}>
            <LocalPhoneRoundedIcon aria-hidden />
            Call
          </SecondaryCtaLink>
        ) : null}
        <SecondaryCtaLink as={Link} to={profilePath} state={{ serviceSearch }}>
          View profile
        </SecondaryCtaLink>
      </CardCTARow>
    </CardWrapper>
  );
}

ProviderCard.propTypes = {
  provider: PropTypes.object.isRequired,
  /** Unsaved search answers handed to the storefront so its contact sheet starts from them. */
  serviceSearch: PropTypes.object.isRequired,
  onRequestQuote: PropTypes.func.isRequired,
};

/**
 * Service search results for one trade. Answers come from the URL (`?task=&timing=&note=`), so the
 * chips stay editable and links are shareable. Providers are fetched once per trade; task, timing,
 * filters and sorting happen client-side. NKY Connect serves Northern Kentucky only, so there is
 * no location filter. Contact info is asked only when the homeowner picks a provider.
 */
const ServiceProviders = ({ serviceType }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const { task, timing, note } = readServiceSearchParams(serviceType, searchParams);
  const tradeLabel = getSearchTradeLabel(serviceType);
  const taskLabel = getSearchTaskLabel(serviceType, task);
  const timingLabel = getSearchTimingLabel(timing);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortBy, setSortBy] = useState('bestMatch');
  const [page, setPage] = useState(1);
  const [contactProvider, setContactProvider] = useState(null);
  const resumeHandledRef = useRef(false);

  const clientSessionId = useMemo(() => {
    if (user) return null;
    try {
      return getOrCreateWizardClientSessionId();
    } catch {
      return null;
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const data = await servicesService.getServices(serviceType, null, null, true, true);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setFetchError(e.message || 'Failed to load providers');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceType]);

  const selection = useMemo(
    () => selectSearchResultRows(rows, { task, timing }),
    [rows, task, timing],
  );

  const cards = useMemo(() => {
    const cardTask = selection.taskRelaxed ? null : task;
    return selection.rows.map((row, index) =>
      mapServiceRowToSearchCard(row, { task: cardTask, timing, order: index }),
    );
  }, [selection, task, timing]);

  const visible = useMemo(
    () => applySearchFiltersAndSort(cards, activeFilters, sortBy),
    [cards, activeFilters, sortBy],
  );

  const pageCount = Math.max(1, Math.ceil(visible.length / SEARCH_RESULTS_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visible.slice(
    (currentPage - 1) * SEARCH_RESULTS_PAGE_SIZE,
    currentPage * SEARCH_RESULTS_PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [activeFilters, sortBy, serviceType, task, timing]);

  /** Unsaved search answers for the contact sheet (it adds note / address, then saves the request). */
  const draftSearch = useMemo(
    () => ({ serviceType, subServiceType: task, timing: timing ?? null, note }),
    [serviceType, task, timing, note],
  );
  /** The same answers as a saveable request, for the waitlist. */
  const searchState = useMemo(() => buildSearchWizardState(draftSearch), [draftSearch]);
  const existingWaitlistRequestId = getPersistedWizardSearchRequestId(
    fingerprintWizardState(searchState),
  );

  // After signing in from the contact sheet, reopen it for the same provider.
  useEffect(() => {
    if (!user || loading || resumeHandledRef.current) return;
    resumeHandledRef.current = true;
    const data = consumeSendInquiryResume();
    const provider = data?.businessId
      ? cards.find((card) => card.BusinessId === data.businessId)
      : null;
    if (provider) setContactProvider(provider);
  }, [user, loading, cards]);

  const additionalRecipients = useMemo(() => {
    if (!contactProvider) return [];
    return visible
      .filter((card) => card.BusinessId !== contactProvider.BusinessId)
      .slice(0, ALSO_SEND_COUNT)
      .map((card) => ({ BusinessId: card.BusinessId, BusinessName: card.BusinessName }));
  }, [visible, contactProvider]);

  const toggleFilter = (id) => {
    setActiveFilters((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id],
    );
  };

  const handlePageChange = (_event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const seo = <SEO {...buildTradeProvidersSeo(serviceType, taskLabel)} />;

  if (loading) {
    return (
      <PageWrapper>
        {seo}
        <ProvidersLoadingState role="status" aria-live="polite" aria-busy="true">
          <NkySpinner size="lg" label="Finding providers" />
          <ProvidersLoadingTitle>Finding {tradeLabel} pros…</ProvidersLoadingTitle>
        </ProvidersLoadingState>
      </PageWrapper>
    );
  }

  const answerChips = [
    {
      key: 'trade',
      kind: 'Trade',
      label: tradeLabel,
      onEdit: () => navigate(buildSearchEntryPath({ note })),
    },
    {
      key: 'task',
      kind: 'Task',
      label: taskLabel || 'All tasks',
      onEdit: () => navigate(buildTaskStepPath(serviceType, { note })),
    },
    {
      key: 'timing',
      kind: 'Timing',
      label: timingLabel || 'Any time',
      onEdit: () => navigate(buildTimingStepPath(serviceType, task, { note })),
    },
  ];

  const count = cards.length;
  let title;
  if (fetchError) title = `${tradeLabel} pros`;
  else if (count === 0) title = `No ${tradeLabel} pros listed yet`;
  else if (task && !selection.taskRelaxed) title = `${count} ${count === 1 ? 'pro' : 'pros'} for ${taskLabel}`;
  else title = `${formatTradeProCount(serviceType, count)} in Northern Kentucky`;

  const showResults = !fetchError && count > 0;

  return (
    <PageWrapper style={{ '--trade': getTradeTheme(serviceType).color }}>
      {seo}
      <HeaderSection>
        <HeaderInner>
          <HeaderTitle>{title}</HeaderTitle>
          <AnswerChipRow>
            {answerChips.map((chip) => (
              <AnswerChip
                key={chip.key}
                onClick={chip.onEdit}
                aria-label={`Change ${chip.kind.toLowerCase()}: ${chip.label}`}
              >
                <span className="answer-chip-kind">{chip.kind}:</span>
                {chip.label}
                <EditRoundedIcon aria-hidden />
              </AnswerChip>
            ))}
          </AnswerChipRow>
        </HeaderInner>
      </HeaderSection>

      {showResults ? (
        <FilterBar>
          <FilterBarInner>
            <FilterLabel>Filter</FilterLabel>
            {SEARCH_RESULT_FILTERS.map((filter) => (
              <FilterPillButton
                key={filter.id}
                type="button"
                $active={activeFilters.includes(filter.id)}
                aria-pressed={activeFilters.includes(filter.id)}
                onClick={() => toggleFilter(filter.id)}
              >
                {filter.label}
              </FilterPillButton>
            ))}
            <SortGroup>
              <SortLabel>Sort:</SortLabel>
              {SEARCH_RESULT_SORTS.map((sort) => (
                <SortButton
                  key={sort.id}
                  type="button"
                  $active={sortBy === sort.id}
                  aria-pressed={sortBy === sort.id}
                  onClick={() => setSortBy(sort.id)}
                >
                  {sort.label}
                </SortButton>
              ))}
            </SortGroup>
          </FilterBarInner>
        </FilterBar>
      ) : null}

      <ResultsContainer>
        {fetchError ? (
          <EmptyState role="alert">
            <EmptyStateTitle>We couldn&apos;t load providers</EmptyStateTitle>
            <EmptyStateText>{fetchError}. Please refresh the page to try again.</EmptyStateText>
          </EmptyState>
        ) : null}

        {!fetchError && count === 0 ? (
          <EmptyMatchWaitlist
            tradeLabel={tradeLabel}
            searchRequestId={existingWaitlistRequestId || ''}
            wizardState={searchState}
            clientSessionId={clientSessionId || ''}
            user={user}
          />
        ) : null}

        {showResults && (selection.taskRelaxed || selection.timingRelaxed) ? (
          <ResultsNotice role="status">
            {selection.taskRelaxed ? (
              <div>
                No pros list <strong>{taskLabel}</strong> yet; here are{' '}
                {formatTradeProCount(serviceType, count)} who may still help.
              </div>
            ) : null}
            {selection.timingRelaxed ? (
              <div>
                None of these pros list availability for <strong>{timingLabel}</strong>, so
                we&apos;re showing everyone. Ask about timing when you reach out.
              </div>
            ) : null}
          </ResultsNotice>
        ) : null}

        {showResults && visible.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>No pros match these filters</EmptyStateTitle>
            <EmptyStateText>Remove a filter to see more results.</EmptyStateText>
          </EmptyState>
        ) : null}

        {showResults && visible.length > 0 ? (
          <>
            <ProvidersGrid>
              {pageItems.map((provider) => (
                <ProviderCard
                  key={provider.BusinessId}
                  provider={provider}
                  serviceSearch={draftSearch}
                  onRequestQuote={setContactProvider}
                />
              ))}
            </ProvidersGrid>
            <ResultsPaginationWrap>
              <ResultsPaginationSummary>
                Showing {(currentPage - 1) * SEARCH_RESULTS_PAGE_SIZE + 1}–
                {Math.min(currentPage * SEARCH_RESULTS_PAGE_SIZE, visible.length)} of{' '}
                {visible.length}
              </ResultsPaginationSummary>
              {pageCount > 1 ? (
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="small"
                />
              ) : null}
            </ResultsPaginationWrap>
          </>
        ) : null}
      </ResultsContainer>

      <ContactForServiceModal
        open={Boolean(contactProvider)}
        onClose={() => setContactProvider(null)}
        businessId={contactProvider?.BusinessId}
        businessName={contactProvider?.BusinessName}
        businessPhone={contactProvider?.BusinessPhone}
        quotingMethodLabels={contactProvider?.quotingMethodLabels}
        hasFreeQuotes={contactProvider?.hasFreeQuotes}
        draftSearch={draftSearch}
        additionalRecipients={additionalRecipients}
        clientSessionId={clientSessionId}
        user={user}
      />
    </PageWrapper>
  );
};

ServiceProviders.propTypes = {
  /** Trade from the route (`/plumbing/providers`, …). */
  serviceType: PropTypes.string.isRequired,
};

export default ServiceProviders;
